import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ─── useSort ──────────────────────────────────────────────────────────────────
// Client-side sort over any array. Returns sorted data + controls.
// Usage: const { sorted, sortKey, sortDir, handleSort } = useSort(data, 'name');
export function useSort(data, defaultKey = null, defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !data?.length) return data || [];
    return [...data].sort((a, b) => {
      let av = a[sortKey] ?? '';
      let bv = b[sortKey] ?? '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, handleSort };
}

// ─── usePagination ────────────────────────────────────────────────────────────
// Slices any array into pages. Resets to page 1 when data changes.
// Usage: const { paged, page, setPage, totalPages } = usePagination(sorted, 15);
export function usePagination(data, pageSize = 15) {
  const [page, setPage] = useState(1);

  // reset when data length changes (e.g. after a refetch)
  const len = data?.length ?? 0;
  useEffect(() => { setPage(1); }, [len]);

  const totalPages = Math.max(1, Math.ceil(len / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = (data || []).slice((safePage - 1) * pageSize, safePage * pageSize);

  return { paged, page: safePage, setPage, totalPages };
}

// ─── SortableHeader ───────────────────────────────────────────────────────────
export function SortableHeader({ label, colKey, sortKey, sortDir, onSort, style }) {
  const active = sortKey === colKey;
  return (
    <th
      onClick={() => onSort(colKey)}
      style={{
        padding: '10px 16px', textAlign: 'left',
        fontSize: 11, fontWeight: 600,
        letterSpacing: '0.5px', textTransform: 'uppercase',
        whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
        color: active ? 'var(--accent)' : 'var(--text-3)',
        transition: 'color 0.15s',
        ...style,
      }}
    >
      {label}
      <span style={{ marginLeft: 5, fontSize: 10, opacity: active ? 1 : 0.35 }}>
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
export function Pagination({ page, totalPages, onPage, total, pageSize }) {
  const { t } = useTranslation('common');
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const btn = (label, target, disabled) => (
    <button
      key={label}
      onClick={() => !disabled && onPage(target)}
      disabled={disabled}
      style={{
        padding: '4px 9px', borderRadius: 4,
        background: 'var(--bg-3)', border: '1px solid var(--border)',
        color: disabled ? 'var(--text-3)' : 'var(--text-1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-mono)', fontSize: 13,
        opacity: disabled ? 0.4 : 1,
      }}
    >{label}</button>
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 16px', borderTop: '1px solid var(--border)',
      fontSize: 11, color: 'var(--text-3)',
    }}>
      <span>{t('items', { from, to, total })}</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {btn('«', 1, page === 1)}
        {btn('‹', page - 1, page === 1)}
        <span style={{ padding: '4px 10px', fontSize: 12, color: 'var(--text-2)' }}>
          {t('pageOfTotal', { page, total: totalPages })}
        </span>
        {btn('›', page + 1, page === totalPages)}
        {btn('»', totalPages, page === totalPages)}
      </div>
    </div>
  );
}

// ─── ModalOverlay ──────────────────────────────────────────────────────────────
// Renders via Portal at document.body → no stacking-context issues.
// Closes on Escape, on backdrop click, locks body scroll.
export function ModalOverlay({ onClose, children, zIndex = 9999 }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.78)',
        zIndex,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, style, className, onClick }) {
  return (
    <div
      onClick={onClick}
      className={className}
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.015) 0%, rgba(255,255,255,0) 60%), var(--bg-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: 20,
        boxShadow: 'var(--shadow-sm)',
        transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        ...style
      }}
      onMouseEnter={onClick ? e => {
        e.currentTarget.style.borderColor = 'var(--border-bright)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow), 0 0 0 1px var(--border-glow)';
      } : undefined}
      onMouseLeave={onClick ? e => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      } : undefined}
    >
      {children}
    </div>
  );
}

// ─── Button ───────────────────────────────────────────────────────────────────
export function Button({ children, onClick, variant = 'primary', size = 'md', disabled, loading, icon: Icon, style }) {
  const styles = {
    primary: {
      background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dim) 100%)',
      color: 'white',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 4px 14px var(--accent-glow), inset 0 1px 0 rgba(255,255,255,0.15)',
    },
    secondary: {
      background: 'var(--bg-3)',
      color: 'var(--text-1)',
      border: '1px solid var(--border-bright)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--text-2)',
      border: '1px solid transparent',
    },
    danger: {
      background: 'linear-gradient(135deg, rgba(248,113,113,0.15) 0%, rgba(248,113,113,0.05) 100%)',
      color: 'var(--red)',
      border: '1px solid rgba(248,113,113,0.35)',
    },
    success: {
      background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.05) 100%)',
      color: 'var(--green)',
      border: '1px solid rgba(16,185,129,0.35)',
    }
  };

  const sizes = {
    sm: { padding: '7px 14px', fontSize: 11 },
    md: { padding: '9px 18px', fontSize: 13 },
    lg: { padding: '13px 26px', fontSize: 14 }
  };

  const hoverGlow = {
    primary:   '0 6px 20px var(--accent-glow-strong), inset 0 1px 0 rgba(255,255,255,0.2)',
    secondary: 'var(--shadow-sm), 0 0 0 1px var(--border-glow)',
    ghost:     'none',
    danger:    '0 4px 14px rgba(248,113,113,0.25)',
    success:   '0 4px 14px rgba(16,185,129,0.25)',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        borderRadius: 'var(--radius-sm)', cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '0.2px',
        opacity: disabled ? 0.45 : 1, transition: 'all 0.2s ease',
        ...styles[variant], ...sizes[size], ...style
      }}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.transform = 'translateY(-1px)';
        if (hoverGlow[variant] !== 'none') e.currentTarget.style.boxShadow = hoverGlow[variant];
        if (variant === 'ghost') { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.color = 'var(--text-1)'; }
      }}
      onMouseLeave={e => {
        if (disabled) return;
        e.currentTarget.style.transform = 'translateY(0)';
        const orig = styles[variant];
        if (orig.boxShadow) e.currentTarget.style.boxShadow = orig.boxShadow;
        else                e.currentTarget.style.boxShadow = 'none';
        if (variant === 'ghost') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; }
      }}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : Icon && <Icon size={14} />}
      {children}
    </button>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ children, color = 'default' }) {
  const colors = {
    default: { bg: 'var(--bg-4)',                  color: 'var(--text-2)',  bd: 'var(--border)' },
    green:   { bg: 'var(--green-glow)',            color: 'var(--green)',   bd: 'rgba(16,185,129,0.3)' },
    red:     { bg: 'var(--red-glow)',              color: 'var(--red)',     bd: 'rgba(248,113,113,0.3)' },
    yellow:  { bg: 'var(--yellow-glow)',           color: 'var(--yellow)',  bd: 'rgba(251,191,36,0.3)' },
    blue:    { bg: 'var(--accent-glow)',           color: '#a78bfa',        bd: 'rgba(139,92,246,0.3)' },
    purple:  { bg: 'var(--accent-glow)',           color: '#c4b5fd',        bd: 'rgba(139,92,246,0.3)' },
    cyan:    { bg: 'var(--accent2-glow)',          color: 'var(--accent2)', bd: 'rgba(34,211,238,0.3)' },
    pink:    { bg: 'var(--accent3-glow)',          color: 'var(--accent3)', bd: 'rgba(244,114,182,0.3)' },
  };
  const c = colors[color] || colors.default;
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700,
      letterSpacing: '0.6px', textTransform: 'uppercase',
      background: c.bg, color: c.color,
      border: `1px solid ${c.bd}`,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {children}
    </span>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────────
export function Input({ label, value, onChange, placeholder, type = 'text', rows, style, name }) {
  const inputStyle = {
    width: '100%', background: 'var(--bg-3)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 13,
    padding: '8px 12px', outline: 'none', transition: 'border-color 0.15s',
    ...style
  };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</label>}
      {rows ? (
        <textarea value={value} onChange={onChange} placeholder={placeholder} name={name} rows={rows} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      ) : (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} name={name} style={inputStyle}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      )}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────
export function Select({ label, value, onChange, options, name }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</label>}
      <select value={value} onChange={onChange} name={name} style={{
        width: '100%', background: 'var(--bg-3)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)', color: 'var(--text-1)',
        fontFamily: 'var(--font-mono)', fontSize: 13, padding: '8px 12px', outline: 'none'
      }}>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 20 }) {
  return <Loader2 size={size} className="animate-spin" style={{ color: 'var(--accent)' }} />;
}

// ─── Empty State ──────────────────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px' }}>
      {Icon && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 72, height: 72, borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--accent-glow) 0%, var(--accent2-glow) 100%)',
          border: '1px solid var(--border)', marginBottom: 20,
          boxShadow: '0 0 32px var(--accent-glow)',
        }}>
          <Icon size={32} style={{ color: 'var(--accent)' }} />
        </div>
      )}
      <div style={{
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20,
        marginBottom: 10, color: 'var(--text-1)',
      }}>{title}</div>
      <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 24, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>{description}</div>
      {action}
    </div>
  );
}

// ─── Page Header ──────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{
      padding: '32px 36px 24px',
      borderBottom: '1px solid var(--border)',
      background:
        'radial-gradient(circle at 0% 0%, var(--accent-glow) 0%, transparent 45%),' +
        'radial-gradient(circle at 100% 0%, var(--accent2-glow) 0%, transparent 45%),' +
        'linear-gradient(180deg, var(--bg-1) 0%, transparent 100%)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16,
      flexWrap: 'wrap', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h1
          className="gradient-text"
          style={{
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30,
            lineHeight: 1.1, letterSpacing: '-0.5px', marginBottom: 6,
          }}
        >
          {title}
        </h1>
        {subtitle && <p style={{ color: 'var(--text-2)', fontSize: 13, maxWidth: 720 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, position: 'relative', zIndex: 1 }}>{actions}</div>}
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon: Icon, color = 'var(--accent)' }) {
  return (
    <Card style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Subtle accent glow in the corner */}
      <div style={{
        position: 'absolute', top: -40, right: -40, width: 120, height: 120,
        borderRadius: '50%', background: color, opacity: 0.06, filter: 'blur(40px)',
        pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
        <div>
          <div style={{
            fontSize: 10, color: 'var(--text-3)', marginBottom: 10,
            fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase',
          }}>{label}</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 36,
            color, lineHeight: 1, letterSpacing: '-1px',
          }}>{value}</div>
        </div>
        {Icon && (
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: `linear-gradient(135deg, ${color}25 0%, ${color}08 100%)`,
            border: `1px solid ${color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 16px ${color}20`,
          }}>
            <Icon size={20} style={{ color }} />
          </div>
        )}
      </div>
    </Card>
  );
}
